import * as firebase from 'firebase/app'
import * as React from 'react'
import { RouteComponentProps } from 'react-router-dom'

import AuthContext from 'context/AuthContext'

import Loader from 'components/Loader'
import { ICompany } from 'interfaces/ICompany'
import { IInvoice } from 'interfaces/IInvoice'

const fonts = {
  'frutiger-light': {
    bold: require('./fonts/Frutiger-Bold.otf'),
    light: require('./fonts/Frutiger-Light.otf'),
  },
}

const css = {
  alxoptr: loadCss('Alxoptr'),
  alex: loadCss('Alex'),
  angelica: loadCss('Angelica'),
  default: loadCss('Default'),
}

function loadCss(style: string = 'Alex') {
  return require(`!!raw-loader!./${style}.css`)
    .default.replace(
      /fonts\/frutiger-light\/light/g,
      fonts['frutiger-light'].light
    )
    .replace(/fonts\/frutiger-light\/bold/g, fonts['frutiger-light'].bold)
}

interface IPrintState {
  invoice?: IInvoice
}

class Print extends React.Component<
  RouteComponentProps<{ company: string; id: string }> & {
    logo: string | null
    style: ICompany['style']
  },
  IPrintState
> {
  public state: IPrintState = {
    invoice: undefined,
  }

  public async componentDidMount() {
    const { currentUser } = firebase.auth()

    const { id, company } = this.props.match.params

    if (this.props.match.params.id && currentUser) {
      const snapshot = await firebase
        .database()
        .ref(`/invoices/${currentUser.uid}/${company}/${id}`)
        .once('value')

      this.setState({ invoice: snapshot.val() })
    }
  }

  get paymentConditions() {
    const { state } = this
    if (!state.invoice) {
      return ''
    }

    const date = new Date(state.invoice.date)
    const expire = new Date(state.invoice.expire)

    return (expire.getTime() - date.getTime()) / 86400
  }

  get invoiceMoms() {
    const {
      state: { invoice },
    } = this

    if (invoice && invoice.items) {
      return invoice.items[0].moms
    }

    return 0
  }

  get moms() {
    const {
      state: { invoice },
    } = this

    if (invoice && invoice.items) {
      return Math.round(
        invoice.items.reduce((a, b) => a + b.sum * (b.moms / 100), 0)
      )
    }

    return 0
  }

  get netto() {
    const {
      state: { invoice },
    } = this

    if (invoice && invoice.items) {
      return Math.round(invoice.items.reduce((a, b) => a + b.sum, 0))
    }

    return 0
  }

  get sum() {
    return this.moms + this.netto
  }

  public format(num: number) {
    return new Intl.NumberFormat('sv-SE', {
      style: 'decimal',
      minimumFractionDigits: 2,
    }).format(num)
  }

  get toPay() {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
    }).format(this.sum)
  }

  public render() {
    const {
      props,
      state,
      paymentConditions,
      invoiceMoms,
      moms,
      netto,
      sum,
      toPay,
      format,
    } = this

    if (!state.invoice) {
      return <Loader />
    }

    return (
      <div className="invoice" id="invoice">
        {props.style && <style>{css[props.style]}</style>}
        {props.logo && <img id="invoiceLogo" src={props.logo} />}

        <div id="invoiceNumber">
          <span className="title">Faktura</span>
          <span className="value">{state.invoice.number}</span>
        </div>

        <div id="invoiceDaysDate">
          <span className="title">Fakturadatum</span>
          <span className="value">{state.invoice.date}</span>
        </div>

        <div id="invoiceDaysExpire">
          <span className="title">Förfallodatum</span>
          <span className="value">{state.invoice.expire}</span>
        </div>

        <div id="invoiceDaysExpireDays">
          <span className="title">Betalningsvillkor</span>
          <span className="value">{paymentConditions}</span>
        </div>

        {state.invoice.client.reference && (
          <div id="invoiceReference">
            <span className="title">Referens</span>
            <span className="value"> {state.invoice.client.reference}</span>
          </div>
        )}

        <div id="invoiceInterest">
          <span className="title">Dröjsmålsränta</span>
          <span className="value"> Enligt Lag</span>
        </div>

        <div id="invoiceRecipient">
          <span className="company">{state.invoice.client.name}</span>
          <span className="adress">{state.invoice.client.adress}</span>
          <span className="place">
            {state.invoice.client.zip} {state.invoice.client.city}
          </span>
        </div>
        <div id="invoiceTableWrapper">
          <table id="invoiceTable">
            <thead>
              <tr className="info">
                <td className="title" title="Beskrivning" />
                <td className="num" title="Antal" />
                <td className="price" title="Pris" />
                <td className="sum" title="Summa" />
                <td className="moms" title="Moms" />
              </tr>
            </thead>
            <tbody>
              {state.invoice.items &&
                state.invoice.items.map((item, index) => (
                  <tr className="item" key={index}>
                    <td className="title">
                      {item.description}
                      {item.text && <div className="text">{item.text}</div>}
                    </td>
                    <td className="num">{item.num}</td>
                    <td className="price">{format(item.price)}</td>
                    <td className="sum">{format(item.sum)}</td>
                    <td className="moms">{item.moms}%</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div id="invoiceMomsPercent">
          <span className="title">Moms</span>
          <span className="value">{invoiceMoms}%</span>
        </div>

        <div id="invoiceSum">
          <span className="title">Pris exkl. moms</span>
          <span className="value">{format(netto)}</span>
        </div>

        <div id="invoiceMoms">
          <span className="title">Moms</span>
          <span className="value">{format(moms)}</span>
        </div>

        <div id="invoiceToPay">
          <span className="title">Summa</span>
          <span className="value">{format(sum)}</span>
        </div>

        <div id="invoiceToPayBig">
          <span className="title">Att Betala</span>
          <span className="value">{toPay}</span>
        </div>

        <div id="invoiceFSkatt">Innehar F-skattsedel</div>

        <div id="invoiceFoot">
          <div id="invoiceFootAdress">
            <span className="title">Adressuppgifter</span>
            <span className="value">{state.invoice.company.name}</span>
            <span className="value">{state.invoice.company.adress}</span>
            <span className="value">
              {state.invoice.company.zip} {state.invoice.company.city}
            </span>
          </div>

          <div id="invoiceFootInfo">
            <span className="title">Kontaktuppgifter</span>
            <span className="value">{state.invoice.company.email}</span>
            {state.invoice.company.web && (
              <span className="value">{state.invoice.company.web}</span>
            )}
            <span className="value">{state.invoice.company.phone}</span>
          </div>

          <div id="invoiceFootMomsReg">
            {state.invoice.company.orgnr && (
              <>
                <span className="title">Org.nr</span>
                <span className="value">{state.invoice.company.orgnr}</span>
              </>
            )}
            <span className="title">Moms.nr</span>
            <span className="value">{state.invoice.company.momsnr}</span>
          </div>

          <div id="invoiceFootBank">
            <span className="title">
              {state.invoice.company.banktype === 'PG' && 'Plusgiro'}
              {state.invoice.company.banktype === 'BG' && 'Bankgira'}
              {state.invoice.company.banktype === 'Konto' && 'Konto'}
            </span>
            <span className="value">{state.invoice.company.banknumber}</span>

            {/* <span className="title"><?=($COMPANY["banktype"] == "PG" ? "Plusgiro" : "Bankgiro")?></span>
					<span className="value"><?=$COMPANY["banknumber"]?></span> */}
          </div>
        </div>
      </div>
    )
  }
}

export default (
  props: RouteComponentProps<{ company: string; id: string }>
) => (
  <AuthContext.Consumer>
    {({ logo, companies }) =>
      companies[props.match.params.company] && (
        <Print
          {...props}
          logo={logo}
          style={companies[props.match.params.company].style}
        />
      )
    }
  </AuthContext.Consumer>
)



// WEBPACK FOOTER //
// ./src/containers/Print/Print.tsx